import styled from '@emotion/styled/macro';
import {
  SvgIcon,
  SvgNames,
  Typography,
} from '@sunrun/experience-ui-components';
/* eslint-disable import/no-restricted-paths -- list-view row composes sibling molecules */
import { ListViewMenuOptions } from './ListViewMenuOptions';
import { ListViewRowMobile } from './ListViewRowMobile';
import {
  FloaterMenuContainer,
  ListViewFixedHeader,
  ListViewMenuItem,
} from './ListViewTable';
/* eslint-enable import/no-restricted-paths */
import {
  PIPELINE_LIST_VIEW_ACTIONS_COLUMN_WIDTH,
  PIPELINE_LIST_VIEW_COLUMNS,
} from 'constants/pipelineListViewColumns';
import {
  getProspectAddress,
  getProspectCreatedDateFormatted,
  getProspectDisplayName,
  getProspectLeadStage,
} from 'helpers/listViewProspectDisplay';
import { Prospect } from 'types/prospect';
import { useClickOutside } from 'hooks/useClickOutside';
import { useListViewSortBreakpoints } from 'hooks/useListViewSortBreakpoints';
import { ListViewColumnDef } from 'types/listViewColumns';

type ListViewHeaderProps = {
  sortValue: string;
  setSortValue: (sortValue: string) => void;
  sortDirection: 'ASC' | 'DESC';
};

const pipelineListViewColumnDefs: ListViewColumnDef[] =
  PIPELINE_LIST_VIEW_COLUMNS.map(({ id, label, width, getCellText }) => ({
    id,
    label,
    sortKey: label,
    width,
    renderCell: (prospect) => <DataCellContent text={getCellText(prospect)} />,
  }));

const ListViewHeader = ({
  sortValue,
  setSortValue,
  sortDirection,
}: ListViewHeaderProps) => (
  <ListViewFixedHeader
    columns={pipelineListViewColumnDefs}
    sortField={sortValue}
    sortDirection={sortDirection}
    onSort={setSortValue}
    actionsColumnWidth={PIPELINE_LIST_VIEW_ACTIONS_COLUMN_WIDTH}
  />
);

type ListViewRowProps = {
  prospect: Prospect;
  isEven: boolean;
  isFloaterMenuOpen: boolean;
  onCloseFloaterMenu: () => void;
  onRowBodyClick: () => void;
  onDotsClick: () => void;
};

const ListViewRow = ({
  prospect,
  isEven,
  isFloaterMenuOpen,
  onCloseFloaterMenu,
  onRowBodyClick,
  onDotsClick,
}: ListViewRowProps) => {
  const createdDateFormatted = getProspectCreatedDateFormatted(prospect);
  const name = getProspectDisplayName(prospect);
  const address = getProspectAddress(prospect);
  const leadStage = getProspectLeadStage(prospect);
  const { isDesktop } = useListViewSortBreakpoints();
  const ref = useClickOutside(onCloseFloaterMenu, isFloaterMenuOpen);

  // Use compact rows below desktop width (avoids fixed-width grid in the 681–879px gap).
  if (!isDesktop) {
    return (
      <ListViewRowMobile
        prospect={prospect}
        isEven={isEven}
        statusPill={leadStage}
        createdDate={createdDateFormatted}
        name={name}
        subtitle={<Typography>{address}</Typography>}
      />
    );
  }

  const stageColumnIndex = PIPELINE_LIST_VIEW_COLUMNS.findIndex(
    (column) => column.id === 'stage',
  );

  return (
    <GridRow
      onClick={(e) => {
        if (isFloaterMenuOpen) return;
        onRowBodyClick();
        e.stopPropagation();
      }}
      isFloaterMenuOpen={isFloaterMenuOpen}
      isEven={isEven}>
      {PIPELINE_LIST_VIEW_COLUMNS.map((column, index) => (
        <DataCell key={column.id} width={column.width}>
          <DataCellContent text={column.getCellText(prospect)} />
          {index === stageColumnIndex && isFloaterMenuOpen && (
            <FloaterMenuContainer ref={ref}>
              <ListViewMenuOptions
                Child={({ onClick, label }) => (
                  <ListViewMenuItem onClick={onClick}>
                    <Typography>{label}</Typography>
                  </ListViewMenuItem>
                )}
                prospect={prospect}
              />
            </FloaterMenuContainer>
          )}
        </DataCell>
      ))}
      {isDesktop && (
        <FlexSpaceBetween
          style={{
            width: PIPELINE_LIST_VIEW_ACTIONS_COLUMN_WIDTH,
            cursor: 'pointer',
          }}
          onClick={(e) => {
            e.stopPropagation();
            onDotsClick();
          }}>
          <SvgIcon name={SvgNames.VerticalDotsSvg} width="24" height="24" />
        </FlexSpaceBetween>
      )}
    </GridRow>
  );
};

const FlexSpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const GridRow = styled.div<{ isFloaterMenuOpen: boolean; isEven: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 16px 32px;

  ${({ isEven }) =>
    isEven &&
    `
    background-color: rgba(31, 38, 71, 0.04);
  `}

  ${({ isFloaterMenuOpen }) =>
    isFloaterMenuOpen &&
    `
    background-color: rgba(165, 201, 255, 0.3);
  `}

  &:last-child {
    border-bottom: 1px solid rgb(208, 204, 195, 20%);
  }
`;

const DataCell = styled(Typography)<{ width?: number }>`
  padding: 12px;
  width: ${(props) => props.width || 100}%;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  position: relative;
`;

const DataCellWrap = styled.span`
  overflow-wrap: break-word;
  word-break: normal;
  min-width: 0;
`;

const DataCellEllipsis = styled.span`
  display: block;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DataCellContent = ({ text }: { text: string }) => {
  const hasSpace = text.includes(' ');
  return hasSpace ? (
    <DataCellWrap>{text}</DataCellWrap>
  ) : (
    <DataCellEllipsis title={text}>{text}</DataCellEllipsis>
  );
};

export { ListViewRow, ListViewHeader };
